import { useGo } from "@refinedev/core";
import { Button } from "@/components/ui/button";
import {
  ShieldX,
  Home,
  ArrowLeft,
  AlertCircle,
  FileQuestion,
} from "lucide-react";

interface ErrorPageProps {
  error?: {
    statusCode?: number;
    message?: string;
  };
}

/**
 * Error page component that handles different error types
 */
export function ErrorPage({ error }: ErrorPageProps) {
  const go = useGo();
  const statusCode = error?.statusCode ?? 500;

  // Determine error type and content
  const errorConfig = {
    403: {
      icon: ShieldX,
      title: "Доступ запрещён",
      description:
        "У вас недостаточно прав для просмотра этой страницы. Обратитесь к администратору для получения необходимых разрешений.",
      iconColor: "text-destructive",
      bgColor: "bg-destructive/10",
    },
    404: {
      icon: FileQuestion,
      title: "Страница не найдена",
      description: "Запрашиваемая страница не существует или была удалена.",
      iconColor: "text-muted-foreground",
      bgColor: "bg-muted",
    },
    500: {
      icon: AlertCircle,
      title: "Ошибка сервера",
      description:
        "Произошла внутренняя ошибка сервера. Попробуйте обновить страницу или повторить позже.",
      iconColor: "text-orange-500",
      bgColor: "bg-orange-500/10",
    },
  };

  const config =
    errorConfig[statusCode as keyof typeof errorConfig] ?? errorConfig[500];
  const Icon = config.icon;

  return (
    <div className="flex min-h-[80vh] flex-col items-center justify-center p-4">
      <div className="text-center space-y-6">
        <div className="flex justify-center">
          <div className={`rounded-full ${config.bgColor} p-6`}>
            <Icon className={`h-16 w-16 ${config.iconColor}`} />
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="text-4xl font-bold tracking-tight">{statusCode}</h1>
          <h2 className="text-xl font-semibold text-muted-foreground">
            {config.title}
          </h2>
          <p className="text-muted-foreground max-w-md mx-auto">
            {config.description}
          </p>
          {error?.message && (
            <p className="text-sm text-muted-foreground mt-2 font-mono bg-muted p-2 rounded">
              {error.message}
            </p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            variant="outline"
            onClick={() => window.history.back()}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Назад
          </Button>
          <Button onClick={() => go({ to: "/" })} className="gap-2">
            <Home className="h-4 w-4" />
            На главную
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * 403 Forbidden page
 */
export function ForbiddenPage() {
  return <ErrorPage error={{ statusCode: 403 }} />;
}

/**
 * 404 Not Found page
 */
export function NotFoundPage() {
  return <ErrorPage error={{ statusCode: 404 }} />;
}
